import React, { useState, useRef, useEffect } from 'react'
import styles from './styles'
import {
  Typography,
  withStyles,
  Box,
  CssBaseline,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import defaultContent, { productTrainingContent, sellingContent, eLearningContent, marketingContent } from './content'
import { INTEREST } from './interests'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Controller, Pagination } from 'swiper/modules'
import { CoreButton } from 'src/components/core'
import Lottie from 'react-lottie'

// Import Swiper styles
import 'swiper/css'
import 'swiper/css/pagination'

const LottieSlide = ({ url, loop = true, isActive }) => {
  const [animationData, setAnimationData] = useState(null)
  const [isStopped, setIsStopped] = useState(true)

  useEffect(() => {
    fetch(url)
      .then(res => res.json())
      .then(data => setAnimationData(data))
      .catch(() => {})
  }, [url])

  useEffect(() => {
    if (isActive && animationData) {
      setIsStopped(true)
      const frame = requestAnimationFrame(() => setIsStopped(false))
      return () => cancelAnimationFrame(frame)
    } else {
      setIsStopped(true)
    }
  }, [isActive, animationData])

  if (!animationData) return null

  return (
    <Lottie
      options={{
        loop: loop,
        autoplay: false,
        animationData,
        rendererSettings: {
          preserveAspectRatio: 'xMidYMid slice',
        },
      }}
      isStopped={isStopped}
      isClickToPauseDisabled
      style={{ width: '100%', height: '100%' }}
    />
  )
}

const OnboardingInformation = props => {
  const {
    pageView,
    setPageView,
    handlePageView,
    onBack,
    subtitle,
    selectedInterest,
    answeredDeliveryStep,
    onComplete,
    classes,
  } = props
  const [controlledSwiper, setControlledSwiper] = useState(null)
  const [loading, setLoading] = useState(false)
  const swiperRef = useRef(null)
  const isCreator = selectedInterest === INTEREST.CREATOR
  // Deck tailored to the Q2 answer. Customer Success, Creator and Marketing have
  // curated decks; L&D/HR answers Q2 but has no deck yet and falls through to
  // the generic default. The "Other" Q2 answer also falls through.
  const deliveryContent =
    (selectedInterest === INTEREST.CUSTOMER_SUCCESS &&
      productTrainingContent[subtitle]) ||
    (isCreator && sellingContent[subtitle]) ||
    (selectedInterest === INTEREST.MARKETING_COMMUNITY &&
      marketingContent[subtitle]) ||
    null
  // A creator who skipped Q2 keeps the authoring deck rather than dropping to the
  // generic one. This is where the old E-learning Authoring interest ended up.
  const creatorFallbackContent = isCreator && !deliveryContent ? eLearningContent : null
  const content = deliveryContent || creatorFallbackContent || defaultContent
  const hasDeliveryContent = !!(deliveryContent || creatorFallbackContent)
  // Answering Q2 skips the free-text step, so this component mounts one pageView
  // earlier. Derive the offset from that, not from whether a deck happens to exist,
  // or the branches without decks land on the wrong slide.
  const slideOffset = answeredDeliveryStep ? 2 : 3
  const slideIndex = pageView - slideOffset
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const goNext = () => {
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slideNext()
    }
  }

  const goPrev = () => {
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slidePrev()
    }
  }

  // New centered layout for Product Training
  if (hasDeliveryContent) {
    return (
      <div className={classes.purposePageRoot}>
        <CssBaseline />
        <Box className={classes.purposeLogo}>
          <Box
            component="img"
            src="/assets/mcg-icon.svg"
            style={{ width: 32, height: 32 }}
          />
          <Box
            component="img"
            src="/assets/mcg-text.svg"
            style={{ width: '130px', height: 32 }}
          />
        </Box>
        <div className={classes.purposeCenterContainer}>
          <div className={classes.slidesWrapper}>
            <Typography onClick={onBack} className={classes.deliveryBackButton}>
              ‹&nbsp;&nbsp;Back
            </Typography>
            <Box mt={3} />
            {subtitle && (
              <Typography className={classes.slidesSubtitle}>
                {subtitle}
              </Typography>
            )}
            <Box mt={3} />
            <div className={classes.slideImageCard}>
              <Swiper
                ref={swiperRef}
                modules={[Controller]}
                spaceBetween={0}
                slidesPerView={1}
                onSlideChange={swiper => setPageView(swiper.activeIndex + slideOffset)}
                onSwiper={setControlledSwiper}
                controller={{ control: controlledSwiper }}
                style={{ width: '100%', height: '100%' }}
              >
                {content.map((item, index) => (
                  <SwiperSlide key={index}>
                    {item.image && typeof item.image === 'object' && item.image.type === 'lottie' ? (
                      <LottieSlide url={item.image.url} loop={item.image.loop !== undefined ? item.image.loop : true} isActive={index === slideIndex} />
                    ) : (
                      <div
                        className={classes.slideImageInner}
                        style={{
                          backgroundImage: `url(${item.image || `/assets/new-purpose-${index + 1}_sep26.png`})`,
                        }}
                      />
                    )}
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
            <Box mt={2} />
            <div className={classes.slidesPagination}>
              {content.map((_, index) => (
                <div
                  key={index}
                  className={
                    index === slideIndex
                      ? classes.slidesDotActive
                      : classes.slidesDot
                  }
                />
              ))}
            </div>
            <Box mt={3} />
            <div style={{ minHeight: '90px' }}>
              <Typography className={classes.slidesHeading}>
                {content[slideIndex].header}
              </Typography>
              <Box mt={1} />
              <Typography className={classes.slidesDescription}>
                {content[slideIndex].description}
              </Typography>
            </div>
            <Box mt={3} />
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <CircularProgress />
              </div>
            ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', width: '100%' }}>
              {slideIndex > 0 && (
                <Typography
                  onClick={() => {
                    setPageView(prev => prev - 1)
                    goPrev()
                  }}
                  style={{
                    fontFamily: 'Inter',
                    fontSize: '16px',
                    fontWeight: 500,
                    lineHeight: '18px',
                    color: '#002E47',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Previous
                </Typography>
              )}
              <CoreButton
                variant="contained"
                size="large"
                fullWidth
                onClick={() => {
                  if (slideIndex === content.length - 1 && onComplete) {
                    setLoading(true)
                    onComplete()
                  } else {
                    handlePageView()
                    goNext()
                  }
                }}
                style={{
                  borderRadius: '10px',
                  height: '40px',
                }}
              >
                {slideIndex === content.length - 1 ? 'Done 🎉' : 'Next →'}
              </CoreButton>
            </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Old two-column layout for other interests, with new background
  return (
    <div className={classes.purposePageRoot}>
      <CssBaseline />
      <Box className={classes.purposeLogo}>
        <Box
          component="img"
          src="/assets/mcg-icon.svg"
          style={{ width: 32, height: 32 }}
        />
        <Box
          component="img"
          src="/assets/mcg-text.svg"
          style={{ width: '130px', height: 32 }}
        />
      </Box>
      <div className={classes.purposeCenterContainer}>
        <div className={classes.onBoardingRoot}>
          <Swiper
            ref={swiperRef}
            modules={isMobile ? [Controller] : [Pagination, Controller]}
            spaceBetween={isMobile ? 0 : 50}
            slidesPerView={1}
            onSlideChange={swiper => setPageView(swiper.activeIndex + slideOffset)}
            scrollbar={{ draggable: true }}
            onSwiper={setControlledSwiper}
            controller={{ control: controlledSwiper }}
            className={classes.onBoardingRoot_swiperWrapper}
          >
            {content.map((_, index) => (
              <SwiperSlide key={index}>
                <div
                  className={classes.onBoardingRoot_leftSide}
                  style={{
                    backgroundImage: `url(/assets/new-purpose-${index + 1}_sep26.png)`,
                  }}
                />
              </SwiperSlide>
            ))}
          </Swiper>

          <div className={classes.onBoardingRoot_rightSide}>
            <div className={classes.onBoardingRoot_rightSide_text}>
              <div>
                <Typography className={classes.onBoardingHeader}>
                  {content[slideIndex].header}
                </Typography>
                <Typography className={classes.onBoardingDescription}>
                  {content[slideIndex].description}
                </Typography>
              </div>

              <Typography
                className={classes.onBoardingExplanation}
                dangerouslySetInnerHTML={{
                  __html: content[slideIndex].explanation,
                }}
              />
              {!isMobile && <div className={classes.onBoarding_FX}></div>}
              {!isMobile && loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                  <CircularProgress />
                </div>
              ) : !isMobile && (
                <div className={classes.onBoarding_buttonGroup}>
                  <CoreButton
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={() => {
                      if (slideIndex === content.length - 1 && onComplete) {
                        setLoading(true)
                        onComplete()
                      } else {
                        handlePageView()
                        goNext()
                      }
                    }}
                  >
                    {content[slideIndex].buttonText}
                  </CoreButton>
                </div>
              )}
            </div>
          </div>

          {isMobile && loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', width: '100%', marginTop: '16px' }}>
              <CircularProgress />
            </div>
          ) : isMobile && (
            <div className={classes.onBoarding_buttonGroup}>
              <CoreButton
                variant="contained"
                fullWidth={true}
                size="large"
                onClick={() => {
                  if (slideIndex === content.length - 1 && onComplete) {
                    setLoading(true)
                    onComplete()
                  } else {
                    handlePageView()
                    goNext()
                  }
                }}
              >
                {content[slideIndex].buttonText}
              </CoreButton>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default withStyles(styles)(OnboardingInformation)
